// =======================================================
// ConnectionAtlas — ultra-wide AI connection corridor
// =======================================================
//
// Built for 10900 x 540 LED output. This scene uses lightweight geometry
// instead of a fullscreen heavy shader: long routes, hubs, and signal
// packets are rendered as LineSegments + Points so cost stays stable on
// very wide canvases.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const NODE_VERT = /* glsl */ `
  attribute float aPulse;
  attribute float aKind;
  uniform float uPxScale;
  uniform float uSize;
  varying float vPulse;
  varying float vKind;
  void main() {
    vPulse = aPulse;
    vKind = aKind;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uSize * uPxScale * mix(0.85, 2.4, clamp(aPulse, 0.0, 1.0));
  }
`;

const NODE_FRAG = /* glsl */ `
  uniform vec3 uAccent;
  uniform vec3 uSecondary;
  uniform vec3 uWarm;
  varying float vPulse;
  varying float vKind;
  void main() {
    vec2 p = gl_PointCoord - 0.5;
    float r = length(p);
    float core = smoothstep(0.28, 0.0, r);
    float halo = smoothstep(0.5, 0.08, r);
    vec3 cool = mix(uSecondary, uAccent, vKind);
    vec3 col = mix(cool, uWarm, smoothstep(0.72, 1.0, vPulse));
    float a = core * (0.65 + vPulse) + halo * vPulse * 0.42;
    gl_FragColor = vec4(col * a, clamp(a, 0.0, 1.0));
  }
`;

const ROUTE_SUBDIV = 18;
const HUBS = 11;
const LANES = 7;

export class ConnectionAtlas extends Generator {
  constructor() {
    super({
      name: 'Connection Atlas',
      category: 'intense',
      schema: {
        speed:     { default: 1.0,  min: 0,   max: 3,   step: 0.01, label: 'Speed' },
        pulse:     { default: 1.0,  min: 0,   max: 2,   step: 0.01, label: 'Signal pulse' },
        density:   { default: 1.0,  min: 0.3, max: 1.4, step: 0.01, label: 'Connection density' },
        laneBend:  { default: 0.18, min: 0,   max: 0.5, step: 0.01, label: 'Lane bend' },
        nodeSize:  { default: 7.0,  min: 2,   max: 18,  step: 0.1,  label: 'Node size (px)' },
        accent:    { default: '#57f7ff', type: 'color', label: 'Signal cyan' },
        secondary: { default: '#8a6dff', type: 'color', label: 'AI violet' },
        warm:      { default: '#ffd06b', type: 'color', label: 'Human spark' },
        bg:        { default: '#05070d', type: 'color', label: 'Background' },
      }
    });
  }

  onEnter(scene, engine) {
    this.scene = scene;
    this.engine = engine;
    this._pxScale = Math.max(1, engine.height / 540);
    this._build(engine);
    engine.setBackground(this.v('bg'));
  }

  _build(engine) {
    this._disposeMeshes();
    this._makeTopology(engine);
    this._makeRoutes();
    this._makeNodes();
  }

  _disposeMeshes() {
    for (const obj of [this._routeMesh, this._nodeMesh, this._hubRingMesh]) {
      if (!obj) continue;
      if (obj.parent) obj.parent.remove(obj);
      obj.geometry?.dispose();
      obj.material?.dispose();
    }
    this._routeMesh = null;
    this._nodeMesh = null;
    this._hubRingMesh = null;
    this._disposables = [];
  }

  _makeTopology(engine) {
    const aspect = engine.width / engine.height;
    const xMin = -aspect * 0.96;
    const xMax = aspect * 0.96;
    const xSpan = xMax - xMin;

    this._hubs = [];
    for (let i = 0; i < HUBS; i++) {
      const u = HUBS === 1 ? 0.5 : i / (HUBS - 1);
      const drift = (this.rand01(i * 19 + 2) - 0.5) * xSpan * 0.035;
      const y = (this.rand01(i * 23 + 7) - 0.5) * 0.66;
      const major = i === Math.floor(HUBS / 2) || i === 1 || i === HUBS - 2;
      this._hubs.push({
        x: xMin + u * xSpan + drift,
        y,
        major,
        phase: this.rand01(i * 31 + 11),
      });
    }

    this._routes = [];
    const density = this.v('density');
    for (let i = 0; i < HUBS - 1; i++) {
      for (let lane = 0; lane < LANES; lane++) {
        if (this.rand01(i * 101 + lane * 17) > density) continue;
        const yLane = ((lane + 0.5) / LANES - 0.5) * 1.55;
        const a = this._hubs[i];
        const b = this._hubs[i + 1];
        this._routes.push({
          a: { x: a.x, y: a.y * 0.45 + yLane * 0.55 },
          b: { x: b.x, y: b.y * 0.45 + yLane * 0.55 },
          bend: (this.rand01(i * 53 + lane * 29) - 0.5) * this.v('laneBend'),
          phase: this.rand01(i * 67 + lane * 13),
          speed: 0.65 + this.rand01(i * 83 + lane * 37) * 1.25,
          warmth: this.rand01(i * 43 + lane * 41),
        });
      }
    }

    for (let i = 0; i < HUBS - 2; i += 2) {
      const a = this._hubs[i];
      const b = this._hubs[i + 2];
      this._routes.push({
        a: { x: a.x, y: a.y },
        b: { x: b.x, y: b.y },
        bend: (this.rand01(i * 89 + 5) - 0.5) * 0.42,
        phase: this.rand01(i * 97 + 3),
        speed: 0.42 + this.rand01(i * 109 + 9) * 0.85,
        warmth: 0.85,
      });
    }
  }

  _curvePoint(route, t) {
    const x = route.a.x + (route.b.x - route.a.x) * t;
    const y = route.a.y + (route.b.y - route.a.y) * t
      + Math.sin(t * Math.PI) * route.bend
      + Math.sin((x * 0.32) + this.seed) * 0.018;
    return { x, y };
  }

  _makeRoutes() {
    const vertsPerRoute = ROUTE_SUBDIV * 2;
    const totalVerts = this._routes.length * vertsPerRoute;
    const positions = new Float32Array(totalVerts * 3);
    const colors = new Float32Array(totalVerts * 3);
    this._routeT = new Float32Array(totalVerts);

    for (let r = 0; r < this._routes.length; r++) {
      const base = r * vertsPerRoute;
      for (let s = 0; s < ROUTE_SUBDIV; s++) {
        const t0 = s / ROUTE_SUBDIV;
        const t1 = (s + 1) / ROUTE_SUBDIV;
        const p0 = this._curvePoint(this._routes[r], t0);
        const p1 = this._curvePoint(this._routes[r], t1);
        const v0 = base + s * 2;
        const v1 = v0 + 1;
        positions[v0 * 3 + 0] = p0.x;
        positions[v0 * 3 + 1] = p0.y;
        positions[v1 * 3 + 0] = p1.x;
        positions[v1 * 3 + 1] = p1.y;
        this._routeT[v0] = t0;
        this._routeT[v1] = t1;
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    this._routeMesh = new THREE.LineSegments(geo, mat);
    this._routeMesh.frustumCulled = false;
    this._routeMesh.renderOrder = 1;
    this.scene.add(this._routeMesh);
    this.track(this._routeMesh);
  }

  _makeNodes() {
    const nodeCount = this._hubs.length + this._routes.length * 2;
    const pos = new Float32Array(nodeCount * 3);
    const pulse = new Float32Array(nodeCount);
    const kind = new Float32Array(nodeCount);
    this._nodeMeta = [];

    let n = 0;
    for (let i = 0; i < this._hubs.length; i++, n++) {
      const h = this._hubs[i];
      pos[n * 3 + 0] = h.x;
      pos[n * 3 + 1] = h.y;
      pulse[n] = h.major ? 0.8 : 0.35;
      kind[n] = h.major ? 0.85 : 0.35;
      this._nodeMeta.push({ phase: h.phase, major: h.major, route: -1, t: 0 });
    }

    for (let r = 0; r < this._routes.length; r++) {
      for (let j = 0; j < 2; j++, n++) {
        const t = j === 0 ? 0.33 : 0.67;
        const p = this._curvePoint(this._routes[r], t);
        pos[n * 3 + 0] = p.x;
        pos[n * 3 + 1] = p.y;
        kind[n] = this._routes[r].warmth;
        this._nodeMeta.push({ phase: this._routes[r].phase + j * 0.31, major: false, route: r, t });
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aPulse', new THREE.BufferAttribute(pulse, 1));
    geo.setAttribute('aKind', new THREE.BufferAttribute(kind, 1));
    const mat = new THREE.ShaderMaterial({
      vertexShader: NODE_VERT,
      fragmentShader: NODE_FRAG,
      uniforms: {
        uPxScale: { value: this._pxScale },
        uSize: { value: this.v('nodeSize') },
        uAccent: { value: new THREE.Color(this.v('accent')) },
        uSecondary: { value: new THREE.Color(this.v('secondary')) },
        uWarm: { value: new THREE.Color(this.v('warm')) },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    this._nodeMesh = new THREE.Points(geo, mat);
    this._nodeMesh.frustumCulled = false;
    this._nodeMesh.renderOrder = 2;
    this.scene.add(this._nodeMesh);
    this.track(this._nodeMesh);
  }

  onResize(w, h) {
    this._pxScale = Math.max(1, h / 540);
    if (this._nodeMesh) this._nodeMesh.material.uniforms.uPxScale.value = this._pxScale;
    this._build(this.engine);
  }

  onParamChange(key, val) {
    if (key === 'bg' && this.engine) this.engine.setBackground(val);
    if (key === 'nodeSize' && this._nodeMesh) this._nodeMesh.material.uniforms.uSize.value = val;
    if ((key === 'accent' || key === 'secondary' || key === 'warm') && this._nodeMesh) {
      const u = this._nodeMesh.material.uniforms;
      if (key === 'accent') u.uAccent.value.set(val);
      if (key === 'secondary') u.uSecondary.value.set(val);
      if (key === 'warm') u.uWarm.value.set(val);
    }
    if (key === 'density' || key === 'laneBend') this._build(this.engine);
  }

  reseed() {
    this.seed = (Math.random() * 0xffffffff) >>> 0;
    if (this.engine) this._build(this.engine);
  }

  update(dt, time) {
    if (!this._routeMesh || !this._nodeMesh) return;

    const accent = new THREE.Color(this.v('accent'));
    const secondary = new THREE.Color(this.v('secondary'));
    const warm = new THREE.Color(this.v('warm'));
    const speed = this.v('speed');
    const pulsePower = this.v('pulse');
    const colors = this._routeMesh.geometry.attributes.color.array;
    const vertsPerRoute = ROUTE_SUBDIV * 2;

    for (let r = 0; r < this._routes.length; r++) {
      const route = this._routes[r];
      const phase = (time * speed * route.speed * 0.16 + route.phase) % 1;
      const base = r * vertsPerRoute;
      const routeColor = route.warmth > 0.72 ? warm : accent;
      for (let v = 0; v < vertsPerRoute; v++) {
        const idx = base + v;
        const t = this._routeT[idx];
        const d = Math.abs(t - phase);
        const wrapD = Math.min(d, 1 - d);
        const packet = Math.exp(-(wrapD * wrapD) / 0.004) * pulsePower;
        const breathe = 0.18 + 0.12 * Math.sin(time * speed * 1.6 + r * 0.47);
        const mixToPacket = Math.min(1, packet);
        colors[idx * 3 + 0] = secondary.r * breathe + (routeColor.r - secondary.r * breathe) * mixToPacket;
        colors[idx * 3 + 1] = secondary.g * breathe + (routeColor.g - secondary.g * breathe) * mixToPacket;
        colors[idx * 3 + 2] = secondary.b * breathe + (routeColor.b - secondary.b * breathe) * mixToPacket;
      }
    }
    this._routeMesh.geometry.attributes.color.needsUpdate = true;

    const pulses = this._nodeMesh.geometry.attributes.aPulse.array;
    for (let i = 0; i < this._nodeMeta.length; i++) {
      const m = this._nodeMeta[i];
      const base = m.major ? 0.45 : 0.12;
      const wave = 0.5 + 0.5 * Math.sin(time * speed * (m.major ? 1.15 : 1.9) + m.phase * 6.283);
      let packetHit = 0;
      if (m.route >= 0) {
        const route = this._routes[m.route];
        const ph = (time * speed * route.speed * 0.16 + route.phase) % 1;
        const d = Math.abs(m.t - ph);
        packetHit = Math.exp(-(Math.min(d, 1 - d) ** 2) / 0.01);
      }
      pulses[i] = Math.min(1.4, base + wave * 0.22 + packetHit * 0.95 * pulsePower);
    }
    this._nodeMesh.geometry.attributes.aPulse.needsUpdate = true;
  }
}
